import React, {
  useState,
  ComponentProps,
  Ref,
  ReactElement,
  ReactNode,
  ChangeEvent,
  useCallback,
  useMemo,
} from 'react';

import PropTypes from 'prop-types';
import styled, { CSSProp } from 'styled-components';

import { useControlled } from '@adsk/alloy-react-helpers';
import { PencilIcon, RenderIconType } from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import InlineEditContext from './InlineEditContext';
import InlineTextInput from './InlineTextInput';
import { InputProps, ValueType, EditProps } from './types';

export type InlineEditProps<TValue extends ValueType> = Omit<
  StylableComponent<HTMLDivElement>,
  'onChange'
> & {
  disabled?: boolean;
  value?: TValue;
  error?: boolean;
  inputProps?: ComponentProps<typeof InlineTextInput> & {
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  };
  inputStyle?: CSSProp;
  onChange?: (val: TValue) => void;
  onEditEnter?: () => void;
  onEditExit?: (val?: TValue, editProps?: EditProps) => void;
  placeholder?: string;
  initialEditMode?: boolean;
  editMode?: boolean;
  renderInput?: (props: InputProps<TValue>) => ReactNode;
  renderValue?: (value: TValue) => ReactNode;
  renderIcon?: RenderIconType;
  showPencilOnHoverOnly?: boolean;
  saveUnmodifiedValue?: boolean;
  cancelOnBlur?: boolean;
};

function defaultValueRenderer(value: ValueType): ReactNode {
  return value;
}

const defaultRenderIcon: RenderIconType = (iconProps) => (
  <PencilIcon {...iconProps} />
);

function isDefined<T>(val: T | undefined | null): val is T {
  return val !== undefined && val !== null;
}

const isValidValue = (val: ValueType) => {
  if (Array.isArray(val)) {
    return val.length !== 0;
  }

  return val !== '';
};

const StyledDiv = styled.div<{
  disabled?: boolean;
  error?: boolean;
  editMode?: boolean;
  notDisabledOrEditMode?: boolean;
  showPencilOnHoverOnly?: boolean;
}>`
  ${theme.typography.labelMedium}
  align-items: center;
  border: 1px solid transparent;
  border-radius: ${theme.borderRadius.small}px;
  cursor: pointer;
  display: flex;
  min-height: 36px;
  max-width: 100%;
  position: relative;
  width: fit-content;

  ${(props) =>
    !props.editMode
      ? {
          padding: `${theme.spacing.xxSmall}px ${theme.spacing.xSmall}px ${theme.spacing.xxSmall}px ${theme.spacing.small}px`,
        }
      : {}}

  ${(props) =>
    props.notDisabledOrEditMode
      ? {
          '&:hover': {
            border: `1px dashed ${theme.colors.adskBlue500}`,
            svg: {
              color: theme.colors.adskBlue700,
              ...(props.showPencilOnHoverOnly && {
                visibility: 'visible',
              }),
            },
          },
          '&:active': {
            border: `1px dashed ${theme.colors.adskBlue500}`,
            svg: {
              color: theme.colors.adskBlue700,
            },
          },
        }
      : {}}

  ${(props) =>
    props.disabled
      ? {
          cursor: 'default',
        }
      : {}}

  ${(props) =>
    !props.disabled && props.editMode
      ? {
          background: theme.colors.white,
          border: `1px solid ${theme.colors.adskBlue500}`,
          cursor: 'default',
          width: '100%',
        }
      : {}}

  ${(props) =>
    !props.disabled && props.error
      ? {
          border: `1px solid ${theme.colors.red600}`,
        }
      : {}}
`;

/**
 * Inline edit provides an easy way for the user to change displayed content and observe the change in context, without navigating to a separate edit page.
 */
const InlineEditWrapped = <TValue extends ValueType>(
  {
    disabled,
    error,
    editMode: controlledEditMode,
    inputProps: allInputProps = {},
    inputStyle,
    onChange,
    onEditEnter,
    onEditExit,
    placeholder,
    initialEditMode = false,
    renderInput = ({ value: inputValue, ...renderInputProps }) => {
      // removing onChange so it doesn't get used for text inputs
      delete renderInputProps.onChange;
      return (
        <InlineTextInput value={inputValue as string} {...renderInputProps} />
      );
    },
    renderValue = defaultValueRenderer,
    renderIcon = defaultRenderIcon,
    value,
    showPencilOnHoverOnly,
    saveUnmodifiedValue,
    cancelOnBlur,
    style,
    ...props
  }: InlineEditProps<TValue>,
  ref: Ref<HTMLDivElement>,
) => {
  const [currentValue, setCurrentValue] = useState(value);
  const [editMode, setEditMode] = useControlled<boolean>(
    initialEditMode,
    controlledEditMode,
  );

  const updateEditMode = useCallback(
    (val: boolean) => {
      if (!controlledEditMode) setEditMode(val);
    },
    [setEditMode, controlledEditMode],
  );

  const updateValue = useCallback(
    (newValue: ValueType) => {
      updateEditMode(false);
      if (saveUnmodifiedValue || newValue !== currentValue) {
        setCurrentValue(newValue as TValue);
        onChange?.(newValue as TValue);
      }
      onEditExit?.(newValue as TValue);
    },
    [currentValue, onChange, onEditExit, saveUnmodifiedValue, updateEditMode],
  );

  const { onChange: inputOnChange, ...inputProps } = allInputProps;

  const context = useMemo(
    () => ({
      updateValue,
      setEditMode: updateEditMode,
      inputOnChange,
      onEditExit: (val: ValueType, editProps?: EditProps) =>
        onEditExit?.(val as TValue, editProps),
    }),
    [inputOnChange, onEditExit, updateEditMode, updateValue],
  );
  const onInputClose = () => {
    inputProps?.onClose?.();
    updateEditMode(false);
    onEditExit?.(currentValue);
  };

  const hasValue = isDefined(value) && isValidValue(value);

  const notDisabledOrEditMode = !disabled && !editMode;

  return (
    <StyledDiv
      disabled={disabled}
      error={error}
      editMode={editMode}
      notDisabledOrEditMode={notDisabledOrEditMode}
      showPencilOnHoverOnly={showPencilOnHoverOnly}
      onClick={useCallback(() => {
        if (notDisabledOrEditMode) {
          updateEditMode(true);
          onEditEnter?.();
        }
      }, [notDisabledOrEditMode, updateEditMode, onEditEnter])}
      css={style}
      {...props}
    >
      {!editMode && (
        <div
          ref={ref}
          css={[
            {
              // Allow consumer to set custom typography
              fontFamily: 'inherit',
              fontWeight: 'inherit',
              fontSize: 'inherit',
              lineHeight: 'inherit',
            },
            {
              color: theme.colors.charcoal900,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
              ...(!hasValue &&
                placeholder && {
                  color: theme.colors.charcoal500,
                }),
            },
          ]}
        >
          {hasValue ? renderValue(value) : placeholder}
        </div>
      )}

      {notDisabledOrEditMode &&
        renderIcon({
          size: 20,
          style: {
            marginLeft: theme.spacing.small,
            pointerEvents: 'none',
            color: theme.colors.charcoal700,
            flexShrink: 0,
            ...(showPencilOnHoverOnly && {
              visibility: 'hidden',
            }),
          },
        })}

      <InlineEditContext.Provider value={context}>
        {editMode &&
          renderInput({
            onChange: updateValue,
            placeholder,
            style: inputStyle,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore this thinks value is TValue | string, which makes no sense because string is valid ValueType.
            value,
            error,
            onClose: onInputClose,
            saveUnmodifiedValue,
            cancelOnBlur,
            ...inputProps,
          })}
      </InlineEditContext.Provider>
    </StyledDiv>
  );
};

const InlineEdit = React.forwardRef(InlineEditWrapped);

InlineEdit.displayName = 'InlineEdit';

InlineEdit.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Style passed to the input element */
  inputStyle: stylePropType,
  /** Props passed to the input element */
  inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** The value of the field.  Provide string unless renderInput is provided */
  value: PropTypes.any,
  /** If set, editMode becomes externally controlled. */
  editMode: PropTypes.bool,
  /** Callback when value is saved */
  onChange: PropTypes.func,
  /** Callback when entering edit mode */
  onEditEnter: PropTypes.func,
  /** Callback when exiting edit mode */
  onEditExit: PropTypes.func,
  /** Placeholder text value */
  placeholder: PropTypes.string,
  /** Toggle for inital edit mode.  Defaults to false */
  initialEditMode: PropTypes.bool,
  /** render custom editor, called with args ({ placeholder, value, onChange }) */
  renderInput: PropTypes.func,
  /** Transform function for display of value */
  renderValue: PropTypes.func,
  /** Renders a custom icon */
  renderIcon: PropTypes.func,
  /** Whether the pencil icon should be hidden by default and only shown on hover state */
  showPencilOnHoverOnly: PropTypes.bool,
  /** If true the initial value can be saved without being modified, onChange will be fired when saving.
   * By default (false) the user must modify the value before it can be saved */
  saveUnmodifiedValue: PropTypes.bool,
  /** If true the initial value will be restored when modified input is blurred */
  cancelOnBlur: PropTypes.bool,
};

export default InlineEdit as <T extends ValueType>(
  p: InlineEditProps<T> & { ref?: Ref<HTMLDivElement> },
) => ReactElement;
